import { useMemo } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import { useMetricPattern } from '../../charts/performance-chart/funcs';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { networkDeviceFilterId, timeRangeFilterId } from '../../filters';

const title = t`Network Device Memory Utilization`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'device',
        metricPatterns: ['mem:usage:'],
        metrics: ['statusMemUtil'],
        time: true,
    },
};

const metricPatternMapping = [
    {
        pattern: 'mem:usage:',
        trendOptions: {
            formatter: FORMATTER_TYPES.PERCENT,
            units: '%',
            forceDecimals: true,
        },
    },
];

const networkDeviceMemUtilChartConfig = {
    yAxis: [
        {
            title: {
                text: t`Utilization (%)`,
            },
            ceiling: 100,
        },
    ],
    tooltip: BuiltInTooltip,
    showGranularity: true,
    statusMetric: 'statusMemUtil',
};

function NetworkDeviceMemoryUtilizationPanel() {
    const data = usePanelDataService(dataSource);
    const firstData = data?.data?.[0];
    const chartTrends = useMetricPattern(metricPatternMapping, firstData);
    const chartDef = useMemo(() => {
        return { ...networkDeviceMemUtilChartConfig, chartTrends };
    }, [chartTrends]);

    if (!data) return null;

    return <PerformanceChart chartDef={chartDef} data={firstData} />;
}

NetworkDeviceMemoryUtilizationPanel.propTypes = panelPropTypes;

NetworkDeviceMemoryUtilizationPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'DeviceMemTrendChart',
    catalogEntry: {
        category: PANEL_CATEGORIES.DEVICE,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Memory utilization trend for a selected network device`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, networkDeviceFilterId],
        optional: [],
    },
})(NetworkDeviceMemoryUtilizationPanel);
